import { type ReactNode } from "react";

import { Navigate } from "react-router-dom";

import { useAuth } from "./AuthProvider";

/**
 * Defense-in-depth gate for `/home/*`. Used in App.tsx to wrap the
 * HomeRoute layout, so EVERY home leaf is protected by construction
 * — adding a new `/home/foo` route to App.tsx without `<AuthGuard>`
 * is impossible because the wrapper sits at the layout level.
 *
 * Three states:
 *   - loading (initial mount, session probe in flight) → splash
 *     placeholder. We DON'T render `children` because doing so
 *     would mount the data-loading effects in protected views and
 *     they'd 401 immediately.
 *   - no session → redirect to `/`.
 *   - has session → render children.
 */
export const AuthGuard = ({ children }: { children: ReactNode }) => {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rook-background text-sm text-muted-foreground">
        Checking session…
      </div>
    );
  }

  if (!session.ok) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

/**
 * Inverse of `<AuthGuard>` — used to wrap the auth routes (login,
 * create-account, forgot-password, reset-password) so a logged-in
 * user can't see them. Mirrors the legacy `authRoutes` redirect
 * branch in `src/middleware.ts`.
 */
export const GuestOnly = ({ children }: { children: ReactNode }) => {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-rook-background text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (session.ok) {
    return <Navigate to="/home" replace />;
  }

  return <>{children}</>;
};
